import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Check, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const languages = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'ta', name: 'Tamil', native: 'தமிழ்' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'es', name: 'Spanish', native: 'Español' },
    { code: 'fr', name: 'French', native: 'Français' },
    { code: 'de', name: 'German', native: 'Deutsch' },
];

const LanguageSelectorModal = ({ isOpen, onSelect }) => {
    const { i18n } = useTranslation();

    const handleSelect = (langCode) => {
        i18n.changeLanguage(langCode);
        onSelect(langCode);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-slate-900/10 backdrop-blur-xl"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        className="relative w-full max-w-sm bg-white border border-slate-100 rounded-[40px] overflow-hidden shadow-2xl shadow-slate-200/50"
                    >
                        {/* Decorative orbs */}
                        <div className="absolute top-0 left-0 w-24 h-24 bg-blue-500/5 rounded-full blur-2xl -translate-x-12 -translate-y-12" />
                        <div className="absolute bottom-0 right-0 w-24 h-24 bg-orange-500/5 rounded-full blur-2xl translate-x-12 translate-y-12" />

                        <div className="p-8 relative z-10">
                            <div className="flex flex-col items-center text-center mb-8">
                                <div className="relative mb-6">
                                    <img
                                        src="/icon-192x192.png"
                                        alt="Logo"
                                        className="w-20 h-20 object-contain rounded-[24px] shadow-lg"
                                    />
                                    <div className="absolute -top-2 -left-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                        <Moon className="w-3 h-3 text-white" />
                                    </div>
                                    <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                                        <Sun className="w-3 h-3 text-white" />
                                    </div>
                                </div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tighter uppercase">Select Language</h2>
                                <p className="text-slate-400 text-[10px] mt-1 font-black uppercase tracking-[0.2em]">Begin your practice</p>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                {languages.map((lang) => {
                                    const isSelected = i18n.language === lang.code;
                                    return (
                                        <button
                                            key={lang.code}
                                            onClick={() => handleSelect(lang.code)}
                                            className={`flex flex-col items-center p-4 rounded-[20px] border-2 transition-all duration-300 relative group ${isSelected
                                                ? 'bg-gradient-to-br from-blue-50 to-orange-50 border-slate-200 shadow-inner'
                                                : 'bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                                                }`}
                                        >
                                            <span className={`text-[13px] font-black uppercase tracking-tight ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>
                                                {lang.name}
                                            </span>
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                                                {lang.native}
                                            </span>
                                            {isSelected && (
                                                <div className="absolute top-2 right-2">
                                                    <div className="bg-slate-900 rounded-full p-0.5 shadow-sm">
                                                        <Check className="w-2 h-2 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => onSelect(i18n.language)}
                                className="w-full mt-8 bg-slate-900 hover:bg-slate-800 text-white font-black py-4.5 rounded-[20px] transition-all active:scale-[0.98] shadow-xl shadow-slate-900/10 text-sm uppercase tracking-widest"
                            >
                                Continue
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default LanguageSelectorModal;
